import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const DEFAULT_FEATURES = [
  "Member Management",
  "Attendance Tracking",
  "Basic Reports",
  "Staff Management",
  "Diet & Workout Plans",
  "Advanced Reports",
  "Priority Support",
  "API Access",
  "Custom Branding",
  "SLA Guarantee",
  "Unlimited Branches",
  "Unlimited Members",
];

const emptyForm = {
  plan_name: "",
  price_monthly: "",
  price_yearly: "",
  max_branches: "",
  max_members: "",
  max_staff: "",
  max_trainers: "",
  features: [],
  is_trial: false,
  trial_days: "",
  is_active: true,
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [customFeature, setCustomFeature] = useState("");

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/super-admin/plans');
      setPlans(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openAddModal = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setFormError(null);
    setCustomFeature("");
    setShowFormModal(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setForm({
      plan_name: plan?.plan_name || "",
      price_monthly: plan?.price_monthly ?? "",
      price_yearly: plan?.price_yearly ?? "",
      max_branches: plan?.max_branches ?? "",
      max_members: plan?.max_members ?? "",
      max_staff: plan?.max_staff ?? "",
      max_trainers: plan?.max_trainers ?? "",
      features: Array.isArray(plan?.features) ? [...plan?.features] : [],
      is_trial: plan?.is_trial ?? false,
      trial_days: plan?.trial_days ?? "",
      is_active: plan?.is_active ?? true,
    });
    setFormError(null);
    setCustomFeature("");
    setShowFormModal(true);
  };

  const openDeleteModal = (plan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleFeature = (feature) => {
    setForm((prev) => ({
      ...prev,
      features: prev?.features?.includes(feature)
        ? prev?.features?.filter((f) => f !== feature)
        : [...(prev?.features || []), feature],
    }));
  };

  const addCustomFeature = () => {
    const trimmed = customFeature?.trim();
    if (!trimmed) return;
    if (!form?.features?.includes(trimmed)) {
      setForm((prev) => ({ ...prev, features: [...(prev?.features || []), trimmed] }));
    }
    setCustomFeature("");
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);
    if (!form?.plan_name?.trim()) return setFormError("Plan name is required.");
    if (form?.price_monthly === "" || isNaN(Number(form?.price_monthly))) return setFormError("Valid monthly price is required.");
    if (form?.price_yearly === "" || isNaN(Number(form?.price_yearly))) return setFormError("Valid yearly price is required.");
    if (form?.max_branches === "" || isNaN(Number(form?.max_branches))) return setFormError("Max branches is required (use -1 for unlimited).");
    if (form?.max_members === "" || isNaN(Number(form?.max_members))) return setFormError("Max members is required (use -1 for unlimited).");
    if (form?.max_staff === "" || isNaN(Number(form?.max_staff))) return setFormError("Max staff is required (use -1 for unlimited).");
    if (form?.max_trainers === "" || isNaN(Number(form?.max_trainers))) return setFormError("Max trainers is required (use -1 for unlimited).");

    setFormLoading(true);
    try {
      const payload = {
        plan_name: form?.plan_name?.trim(),
        price_monthly: Number(form?.price_monthly),
        price_yearly: Number(form?.price_yearly),
        max_branches: Number(form?.max_branches),
        max_members: Number(form?.max_members),
        max_staff: Number(form?.max_staff),
        max_trainers: Number(form?.max_trainers),
        features: form?.features || [],
        is_trial: form?.is_trial,
        trial_days: form?.is_trial ? (Number(form?.trial_days) || null) : null,
        is_active: form?.is_active,
      };

      const cleanPayload = payload;
      if (editingPlan) {
        await api.put(`/super-admin/plans/${editingPlan?.id}`, cleanPayload);
      } else {
        await api.post('/super-admin/plans', cleanPayload);
      }

      await fetchPlans();
      setShowFormModal(false);
    } catch (err) {
      setFormError(err?.message || "An error occurred.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;
    setFormLoading(true);
    try {
      await api.delete(`/super-admin/plans/${deletingPlan?.id}`);
      setPlans((prev) => prev?.filter((p) => p?.id !== deletingPlan?.id));
      setShowDeleteModal(false);
      setDeletingPlan(null);
    } catch (err) {
      setError(err?.message);
    } finally {
      setFormLoading(false);
    }
  };

  const filteredPlans = plans?.filter((p) =>
    p?.plan_name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const formatLimit = (val) => (val === -1 ? "Unlimited" : val);

  const allFeatureOptions = [
    ...DEFAULT_FEATURES,
    ...(form?.features?.filter((f) => !DEFAULT_FEATURES?.includes(f)) || []),
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-base font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Subscription Plans
            </h2>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
              {filteredPlans?.length} plan{filteredPlans?.length !== 1 ? "s" : ""} · define limits and features for vendors
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search plans..."
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="pl-8 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-44"
              />
            </div>
            <button
              onClick={openAddModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
            >
              <Icon name="Plus" size={13} color="white" />
              New Plan
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md text-xs text-[var(--color-error)] font-[var(--font-caption)]">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--color-muted)]/40 border-b border-[var(--color-border)]">
                {["Plan Name", "Monthly", "Yearly", "Branches", "Members", "Staff", "Trainers", "Features", "Status", "Actions"]?.map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 })?.map((_, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)]">
                    {Array.from({ length: 10 })?.map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 bg-[var(--color-muted)] rounded animate-pulse w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredPlans?.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="Package" size={28} color="var(--color-muted-foreground)" />
                      <p>No subscription plans found.</p>
                      <button onClick={openAddModal} className="text-[var(--color-primary)] underline text-xs">Create your first plan</button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPlans?.map((plan) => (
                  <tr key={plan?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors">
                    <td className="px-4 py-3 font-[var(--font-caption)]">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-[var(--color-foreground)]">{plan?.plan_name}</span>
                        {plan?.is_trial && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[var(--color-warning)]/12 text-[var(--color-warning)] whitespace-nowrap">
                            TRIAL · {plan?.trial_days}d
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-[var(--font-data)] text-[var(--color-foreground)]">
                      ${Number(plan?.price_monthly)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 font-[var(--font-data)] text-[var(--color-foreground)]">
                      ${Number(plan?.price_yearly)?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-foreground)]">{formatLimit(plan?.max_branches)}</td>
                    <td className="px-4 py-3 text-[var(--color-foreground)]">{formatLimit(plan?.max_members)}</td>
                    <td className="px-4 py-3 text-[var(--color-foreground)]">{formatLimit(plan?.max_staff)}</td>
                    <td className="px-4 py-3 text-[var(--color-foreground)]">{formatLimit(plan?.max_trainers)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(Array.isArray(plan?.features) ? plan?.features : [])?.slice(0, 3)?.map((f) => (
                          <span key={f} className="px-1.5 py-0.5 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded text-[10px] font-[var(--font-caption)]">
                            {f}
                          </span>
                        ))}
                        {(plan?.features?.length || 0) > 3 && (
                          <span className="px-1.5 py-0.5 bg-[var(--color-muted)] text-[var(--color-muted-foreground)] rounded text-[10px]">
                            +{plan?.features?.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold font-[var(--font-caption)]"
                        style={{
                          backgroundColor: plan?.is_active ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                          color: plan?.is_active ? "var(--color-success)" : "var(--color-error)",
                        }}
                      >
                        {plan?.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEditModal(plan)}
                          className="p-1.5 rounded-md hover:bg-[var(--color-primary)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)] transition-colors"
                          title="Edit"
                        >
                          <Icon name="Pencil" size={13} color="currentColor" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(plan)}
                          className="p-1.5 rounded-md hover:bg-[var(--color-error)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-error)] transition-colors"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={13} color="currentColor" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add/Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !formLoading && setShowFormModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
              <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                {editingPlan ? "Edit Plan" : "Create Subscription Plan"}
              </h3>
              <button onClick={() => !formLoading && setShowFormModal(false)} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="p-5 space-y-4">
              {formError && (
                <div className="p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md text-xs text-[var(--color-error)]">
                  {formError}
                </div>
              )}

              {/* Plan Name + Status */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Plan Name *</label>
                  <input
                    name="plan_name"
                    value={form?.plan_name}
                    onChange={handleFormChange}
                    placeholder="e.g. Pro"
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={form?.is_active}
                      onChange={handleFormChange}
                      className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-ring)]"
                    />
                    <span className="text-xs font-medium text-[var(--color-foreground)]">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_trial"
                      checked={form?.is_trial}
                      onChange={handleFormChange}
                      className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-warning)] focus:ring-[var(--color-ring)]"
                    />
                    <span className="text-xs font-medium text-[var(--color-foreground)]">Trial Plan</span>
                  </label>
                </div>
              </div>

              {/* Trial Days */}
              {form?.is_trial && (
                <div className="w-40">
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Trial Duration (days)</label>
                  <input
                    name="trial_days"
                    type="number"
                    min="1"
                    value={form?.trial_days}
                    onChange={handleFormChange}
                    placeholder="14"
                    className="w-full px-3 py-2 text-xs border border-[var(--color-warning)]/50 rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-warning)]/40"
                  />
                </div>
              )}

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Monthly Price ($) *</label>
                  <input
                    name="price_monthly"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form?.price_monthly}
                    onChange={handleFormChange}
                    placeholder="49.00"
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Yearly Price ($) *</label>
                  <input
                    name="price_yearly"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form?.price_yearly}
                    onChange={handleFormChange}
                    placeholder="490.00"
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
              </div>

              {/* Limits */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-foreground)] mb-2">Limits <span className="font-normal text-[var(--color-muted-foreground)]">(use -1 for unlimited)</span></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { name: "max_branches", label: "Max Branches" },
                    { name: "max_members", label: "Max Members" },
                    { name: "max_staff", label: "Max Staff" },
                    { name: "max_trainers", label: "Max Trainers" },
                  ]?.map(({ name, label }) => (
                    <div key={name}>
                      <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">{label} *</label>
                      <input
                        name={name}
                        type="number"
                        min="-1"
                        value={form?.[name]}
                        onChange={handleFormChange}
                        placeholder="-1"
                        className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-foreground)] mb-2">Features</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {allFeatureOptions?.map((feature) => (
                    <label key={feature} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form?.features?.includes(feature)}
                        onChange={() => toggleFeature(feature)}
                        className="w-3.5 h-3.5 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-ring)]"
                      />
                      <span className="text-xs text-[var(--color-foreground)]">{feature}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customFeature}
                    onChange={(e) => setCustomFeature(e?.target?.value)}
                    onKeyDown={(e) => e?.key === "Enter" && (e?.preventDefault(), addCustomFeature())}
                    placeholder="Add custom feature..."
                    className="flex-1 px-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                  <button
                    type="button"
                    onClick={addCustomFeature}
                    className="px-3 py-1.5 text-xs bg-[var(--color-muted)] text-[var(--color-foreground)] rounded-md hover:bg-[var(--color-border)] transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !formLoading && setShowFormModal(false)}
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-xs font-medium hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
                >
                  {formLoading ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
                  ) : (
                    <>{editingPlan ? "Update Plan" : "Create Plan"}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !formLoading && setShowDeleteModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="Trash2" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Delete Plan?</h3>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Delete <strong>{deletingPlan?.plan_name}</strong>? This cannot be undone. Vendors currently on this plan will lose their assignment.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={formLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {formLoading ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
